// El panel de Moodboard del modo Modelo — ADR-2026-10-18-moodboard-en-el-modo-modelo.
//
// Las carpetas de Mi marca → Moodboards, para elegir una ENTERA (el clip de la carpeta, o
// "Elegir toda la carpeta" adentro) o algunas de sus fotos (el tilde de cada una), y para
// armarlas acá mismo: carpeta nueva, fotos nuevas. La grilla de carpetas es la misma
// `GrillaDeCarpetas` de Mi marca y de la biblioteca del Agente, con su alta, renombre,
// color y borrado.
//
// Se elige UNA carpeta (con todas sus fotos o algunas): elegir otra la reemplaza.
//
// SÓLO SE VE: lo elegido todavía no viaja a la generación. Lo conecta Mati más adelante,
// y ahí también entra a `camposDeModo` + el controlador + las skills (ver la ADR).

import { useEffect, useRef, useState } from 'react';
import { Check, CheckCheck, ChevronLeft, ImagePlus, Loader2, Plus } from 'lucide-react';
import CachedImg from '../../../components/CachedImg';
import GrillaDeCarpetas from '../../../components/moodboards/GrillaDeCarpetas';
import VacioDeEstilos from '../../../components/moodboards/VacioDeEstilos';
import { AccionCard } from '../components/AccionCard';
import {
  cambiarColorDeMoodboard,
  crearMoodboard,
  eliminarMoodboard,
  fetchFotos,
  fetchMoodboards,
  FOTOS_EN_PORTADA,
  renombrarMoodboard,
  subirFoto,
  type Moodboard,
  type MoodboardFoto,
} from '../moodboardsApi';
import { alternarFoto, alternarTodas, estanTodas } from '../../../chat-atlas/ui/seleccionDeMoodboard';

/** Lo elegido: una carpeta, entera o con algunas fotos. */
export interface SeleccionDeMoodboard {
  carpetaId: string;
  nombre: string;
  /** Cuántas fotos tiene la carpeta. */
  cantidadFotos: number;
  /** Las elegidas, en el orden de la carpeta. `null` = la carpeta entera. */
  fotoIds: string[] | null;
  /** Hasta cuatro miniaturas, para la card. */
  miniaturas: string[];
}

const fotos = (n: number) => (n === 1 ? '1 foto' : `${n} fotos`);

/** Lo que dice la card debajo del nombre: "Toda la carpeta · 24 fotos" o "18 de 24 fotos". */
export const detalleDeSeleccion = (s: SeleccionDeMoodboard): string =>
  s.fotoIds ? `${s.fotoIds.length} de ${fotos(s.cantidadFotos)}` : `Toda la carpeta · ${fotos(s.cantidadFotos)}`;

/** Tres por fila, como los otros pickers del panel lateral (`COLUMNAS_PANEL_LATERAL`). */
const GRILLA = 'grid grid-cols-3 gap-x-3 gap-y-4';
/** Cuántas miniaturas lleva la card: el mosaico de 2×2. */
const MINIATURAS = 4;

export const PanelMoodboardModelo: React.FC<{
  companyId: string | null;
  /** La marca activa: las carpetas son de ella. */
  brandId: string | null;
  usuarioId: string | null;
  seleccion: SeleccionDeMoodboard | null;
  onCambiarSeleccion: (s: SeleccionDeMoodboard | null) => void;
}> = ({ companyId, brandId, usuarioId, seleccion, onCambiarSeleccion }) => {
  const [carpetas, setCarpetas] = useState<Moodboard[] | null>(null);
  const [abierta, setAbierta] = useState<Moodboard | null>(null);
  /** La carpeta abierta, para descartar lo que llega tarde de otra. */
  const abiertaRef = useRef<string | null>(null);
  const [fotosAbiertas, setFotosAbiertas] = useState<MoodboardFoto[] | null>(null);
  const [subiendo, setSubiendo] = useState(0);
  /** Lo recién subido, para marcarlo (ver el efecto de abajo). */
  const [porMarcar, setPorMarcar] = useState<{ carpetaId: string; ids: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const avisar = (e: unknown, quePasaba: string) => {
    console.error(`[PanelMoodboardModelo] ${quePasaba}`, e);
    setError(e instanceof Error && e.message ? e.message : `No pudimos ${quePasaba}. Probá de nuevo.`);
  };

  useEffect(() => {
    if (!companyId) return;
    let vivo = true;
    fetchMoodboards({ companyId, brandId })
      .then((lista) => {
        if (vivo) setCarpetas(lista);
      })
      .catch((e) => {
        if (!vivo) return;
        avisar(e, 'cargar los estilos');
        setCarpetas([]);
      });
    return () => {
      vivo = false;
    };
  }, [companyId, brandId]);

  const abrir = async (carpeta: Moodboard) => {
    if (!companyId) return;
    setError(null);
    setAbierta(carpeta);
    abiertaRef.current = carpeta.id;
    setFotosAbiertas(null);
    try {
      const lista = await fetchFotos({ companyId, moodboardId: carpeta.id });
      if (abiertaRef.current === carpeta.id) setFotosAbiertas(lista);
    } catch (e) {
      avisar(e, 'cargar las fotos');
      if (abiertaRef.current === carpeta.id) setFotosAbiertas([]);
    }
  };
  const volver = () => {
    setAbierta(null);
    abiertaRef.current = null;
    setFotosAbiertas(null);
    setError(null);
  };

  // ── Elegir. Las fotos marcadas de una carpeta: las de la selección si es ésta (todas si
  //    la eligió entera), ninguna si no.
  const marcadasDe = (carpeta: Moodboard, lista: MoodboardFoto[]): Set<string> =>
    seleccion?.carpetaId === carpeta.id ? new Set(seleccion.fotoIds ?? lista.map((f) => f.id)) : new Set();
  /** Las fotos marcadas pasan a ser la selección; sin ninguna, no queda nada elegido. */
  const fijar = (carpeta: Moodboard, lista: MoodboardFoto[], marcadas: Set<string>) => {
    const elegidas = lista.filter((f) => marcadas.has(f.id));
    if (elegidas.length === 0) {
      if (seleccion?.carpetaId === carpeta.id) onCambiarSeleccion(null);
      return;
    }
    onCambiarSeleccion({
      carpetaId: carpeta.id,
      nombre: carpeta.nombre,
      cantidadFotos: lista.length,
      fotoIds: elegidas.length === lista.length ? null : elegidas.map((f) => f.id),
      miniaturas: elegidas.slice(0, MINIATURAS).map((f) => f.thumbUrl),
    });
  };
  /** La carpeta entera desde su clip, sin abrirla. Una vacía se abre, para subirle fotos. */
  const elegirEntera = (carpeta: Moodboard) => {
    if (carpeta.cantidadFotos === 0) {
      void abrir(carpeta);
      return;
    }
    onCambiarSeleccion({
      carpetaId: carpeta.id,
      nombre: carpeta.nombre,
      cantidadFotos: carpeta.cantidadFotos,
      fotoIds: null,
      miniaturas: carpeta.portada.slice(0, MINIATURAS),
    });
  };

  /** Carpeta nueva —desde la grilla o desde el vacío—: se ABRE, porque lo que sigue es
   *  subirle fotos. */
  const crearCarpeta = async (nombre: string) => {
    if (!companyId) return;
    try {
      const carpeta = await crearMoodboard({ companyId, brandId, nombre, userId: usuarioId });
      setCarpetas((prev) => [carpeta, ...(prev ?? [])]);
      setAbierta(carpeta);
      abiertaRef.current = carpeta.id;
      setFotosAbiertas([]);
    } catch (e) {
      avisar(e, 'crear la carpeta');
    }
  };

  // ── Subir fotos a la carpeta abierta: de a una, como en Mi marca, y cada una entra a la
  //    grilla en cuanto termina.
  const agregarFotos = async (archivos: File[]) => {
    const carpeta = abierta;
    if (!companyId || !carpeta || archivos.length === 0) return;
    setError(null);
    setSubiendo((n) => n + archivos.length);
    const nuevas: string[] = [];
    for (const file of archivos) {
      try {
        const foto = await subirFoto({ companyId, moodboardId: carpeta.id, file });
        nuevas.push(foto.id);
        if (abiertaRef.current === carpeta.id) setFotosAbiertas((prev) => [foto, ...(prev ?? [])]);
        setCarpetas(
          (prev) =>
            prev &&
            prev.map((c) =>
              c.id === carpeta.id
                ? { ...c, cantidadFotos: c.cantidadFotos + 1, portada: [foto.thumbUrl, ...c.portada].slice(0, FOTOS_EN_PORTADA) }
                : c,
            ),
        );
      } catch (e) {
        avisar(e, 'subir la foto');
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
    if (nuevas.length > 0) setPorMarcar({ carpetaId: carpeta.id, ids: nuevas });
  };
  // Lo recién subido queda marcado: se subió para usarlo. Salvo que la elegida sea OTRA
  // carpeta: se elige una sola, y llenar ésta no tiene por qué cambiar aquélla. En un efecto
  // para leer la selección de ahora, no la de cuando empezó la subida.
  useEffect(() => {
    if (!porMarcar) return;
    setPorMarcar(null);
    if (!abierta || abierta.id !== porMarcar.carpetaId || !fotosAbiertas) return;
    if (seleccion && seleccion.carpetaId !== abierta.id) return;
    const marcadas = marcadasDe(abierta, fotosAbiertas);
    for (const id of porMarcar.ids) marcadas.add(id);
    fijar(abierta, fotosAbiertas, marcadas);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [porMarcar]);

  const aviso = error && (
    <p role="status" className="mb-3 text-[12.5px] text-[#C33B36]">
      {error}
    </p>
  );

  if (!companyId || carpetas === null) {
    return (
      <div className="flex items-center justify-center py-14">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400" aria-hidden="true" />
      </div>
    );
  }

  // ── Sin carpetas: qué sale de armarlas, con la primera adentro
  //    (ADR-2026-10-19-el-vacio-de-estilos-muestra-el-resultado).
  if (!abierta && carpetas.length === 0) {
    return (
      <div>
        {aviso}
        <VacioDeEstilos angosto onCrear={crearCarpeta} />
      </div>
    );
  }

  // ── Las carpetas ──
  if (!abierta) {
    return (
      <div>
        {aviso}
        <GrillaDeCarpetas
          carpetas={carpetas}
          grillaClassName={GRILLA}
          onAbrir={(c) => void abrir(c)}
          onAdjuntar={elegirEntera}
          adjuntada={(id) => seleccion?.carpetaId === id}
          rotuloAdjuntada={() => (seleccion?.fotoIds ? `${seleccion.fotoIds.length} de ${seleccion.cantidadFotos}` : 'Elegida')}
          tituloAdjuntar="Elegir la carpeta entera"
          onCrear={crearCarpeta}
          onRenombrar={async (id, nombre) => {
            const antes = carpetas;
            setCarpetas((prev) => prev && prev.map((c) => (c.id === id ? { ...c, nombre } : c)));
            if (seleccion?.carpetaId === id) onCambiarSeleccion({ ...seleccion, nombre });
            try {
              await renombrarMoodboard(id, nombre);
            } catch (e) {
              setCarpetas(antes);
              avisar(e, 'guardar el nombre');
            }
          }}
          onCambiarColor={async (id, color) => {
            const antes = carpetas;
            setCarpetas((prev) => prev && prev.map((c) => (c.id === id ? { ...c, color } : c)));
            try {
              await cambiarColorDeMoodboard(id, color);
            } catch (e) {
              setCarpetas(antes);
              avisar(e, 'guardar el color');
            }
          }}
          onEliminar={async (id) => {
            try {
              await eliminarMoodboard({ companyId, moodboardId: id });
              setCarpetas((prev) => prev && prev.filter((c) => c.id !== id));
              if (seleccion?.carpetaId === id) onCambiarSeleccion(null);
            } catch (e) {
              avisar(e, 'eliminar la carpeta');
            }
          }}
        />
        <p className="mt-4 text-[12.5px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">
          Elegí una carpeta entera con el clip, o abrila para elegir algunas fotos.
        </p>
      </div>
    );
  }

  // ── Las fotos de una carpeta ──
  const marcadas = fotosAbiertas ? marcadasDe(abierta, fotosAbiertas) : new Set<string>();
  const todasMarcadas = fotosAbiertas ? estanTodas(marcadas, fotosAbiertas) : false;
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={volver}
          className="inline-flex items-center gap-1 rounded-full py-1.5 pl-2 pr-3 text-[13px] font-semibold text-brand-primary transition-colors hover:bg-[rgb(var(--brand-primary-rgb)_/_0.095)]"
        >
          <ChevronLeft className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
          Estilos
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-semibold text-brand-black">{abierta.nombre}</p>
          {fotosAbiertas && fotosAbiertas.length > 0 && (
            <p className="text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">
              {marcadas.size} de {fotosAbiertas.length} elegidas
            </p>
          )}
        </div>
        {/* La carpeta entera de un toque, y después se desmarca lo que no va. */}
        {fotosAbiertas && fotosAbiertas.length > 0 && (
          <button
            type="button"
            onClick={() => fijar(abierta, fotosAbiertas, alternarTodas(marcadas, fotosAbiertas))}
            className={`inline-flex shrink-0 items-center gap-1.5 rounded-full py-1.5 px-3 text-[12.5px] font-semibold transition-colors ${
              todasMarcadas
                ? 'bg-neutral-100 text-[rgb(var(--brand-black-rgb)_/_0.9)] hover:bg-neutral-200'
                : 'bg-brand-primary text-white hover:bg-brand-primary-hover'
            }`}
          >
            <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
            {todasMarcadas ? 'Quitar todas' : 'Elegir toda la carpeta'}
          </button>
        )}
      </div>
      {aviso}

      <input
        ref={inputRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const archivos = Array.from(e.target.files ?? []).filter(
            (f) => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name),
          );
          e.target.value = '';
          void agregarFotos(archivos);
        }}
      />

      {fotosAbiertas === null ? (
        <div className="flex items-center justify-center py-14">
          <Loader2 className="h-5 w-5 animate-spin text-neutral-400" aria-hidden="true" />
        </div>
      ) : (
        <div className={GRILLA}>
          <AccionCard
            className="aspect-square w-full"
            label="Agregar fotos"
            sublabel="Referencias de estilo"
            icon={subiendo > 0 ? <ImagePlus className="h-5 w-5" /> : <Plus className="h-5 w-5" strokeWidth={2.5} />}
            loading={subiendo > 0}
            loadingLabel={`Subiendo ${subiendo}…`}
            onClick={() => {
              if (subiendo > 0) return;
              setError(null);
              inputRef.current?.click();
            }}
          />
          {/* Las que están subiendo, adelante: aparecen donde van a quedar. */}
          {Array.from({ length: subiendo }, (_, i) => (
            <div key={`subiendo-${i}`} className="flex aspect-square items-center justify-center rounded-xl bg-[#F0F2F7]">
              <Loader2 className="h-4 w-4 animate-spin text-[rgb(var(--brand-black-rgb)_/_0.3)]" aria-hidden="true" />
            </div>
          ))}
          {fotosAbiertas.map((foto, i) => {
            const marcada = marcadas.has(foto.id);
            return (
              <button
                key={foto.id}
                type="button"
                onClick={() => fijar(abierta, fotosAbiertas, alternarFoto(marcadas, foto.id))}
                aria-pressed={marcada}
                aria-label={`Foto ${i + 1} de ${abierta.nombre}`}
                className="group relative block aspect-square w-full overflow-hidden rounded-xl bg-[#F0F2F7]"
              >
                <CachedImg src={foto.thumbUrl} alt="" className="h-full w-full object-cover" />
                {marcada ? (
                  <>
                    <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-inset ring-brand-primary" />
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute right-1.5 top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-white shadow-sm"
                    >
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                  </>
                ) : (
                  <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-inset ring-transparent transition-shadow group-hover:ring-brand-primary/40" />
                )}
              </button>
            );
          })}
        </div>
      )}
      {fotosAbiertas && fotosAbiertas.length === 0 && subiendo === 0 && (
        <p className="pt-4 text-center text-[13px] text-[rgb(var(--brand-black-rgb)_/_0.45)]">
          Esta carpeta todavía no tiene fotos. Subí las referencias de estilo de esta idea.
        </p>
      )}
    </div>
  );
};

export default PanelMoodboardModelo;
